import { useEffect, useRef, useState } from "react";
import { FaPlusSquare } from "react-icons/fa";
import { IoTrashOutline } from "react-icons/io5";
import { CiEdit } from "react-icons/ci";
import { RiSave3Fill } from "react-icons/ri";
import { ImCancelCircle } from "react-icons/im";
import { toast } from "react-toastify";
import RightTimeApi from "@/API";
import Modal from "@modalSrc/Modal";
import {
  isReleaseFormDirty,
  releaseHasRequiredVersions,
  toDateInputValue,
  todayDateInputValue,
  toReleaseWritePayload,
  type ReleaseChangeFormRow,
  type ReleaseHistoryItem,
  type ReleaseWriteForm,
} from "@/utils/releaseHistory";

type ReleaseEditModalProps = {
  isOpen: boolean;
  release: ReleaseHistoryItem | null;
  onClose: () => void;
  onSaved: (saved: ReleaseHistoryItem) => void;
  updateSpinner?: ((show: boolean) => void) | null;
};

type EditingChange = {
  localId: number;
  description: string;
  isNew: boolean;
};

function emptyForm(): ReleaseWriteForm {
  return {
    releaseDate: todayDateInputValue(),
    frontendVersion: "",
    backendVersion: "",
    changes: [],
  };
}

function formFromRelease(
  release: ReleaseHistoryItem | null,
  nextLocalId: () => number
): ReleaseWriteForm {
  if (!release) return emptyForm();
  return {
    releaseDate: toDateInputValue(release.releaseDate) || todayDateInputValue(),
    frontendVersion: release.frontendVersion ?? "",
    backendVersion: release.backendVersion ?? "",
    changes: (release.changes ?? []).map((c) => ({
      localId: nextLocalId(),
      description: c.description ?? "",
    })),
  };
}

function cloneForm(form: ReleaseWriteForm): ReleaseWriteForm {
  return {
    ...form,
    changes: form.changes.map((c) => ({ ...c })),
  };
}

export default function ReleaseEditModal({
  isOpen,
  release,
  onClose,
  onSaved,
  updateSpinner = null,
}: ReleaseEditModalProps) {
  const [form, setForm] = useState<ReleaseWriteForm>(emptyForm());
  const [baseline, setBaseline] = useState<ReleaseWriteForm>(emptyForm());
  const [editingChange, setEditingChange] = useState<EditingChange | null>(null);
  const nextLocalIdRef = useRef(1);

  useEffect(() => {
    if (!isOpen) return;
    nextLocalIdRef.current = 1;
    const nextLocalId = () => nextLocalIdRef.current++;
    const initial = formFromRelease(release, nextLocalId);
    setForm(initial);
    setBaseline(cloneForm(initial));
    setEditingChange(null);
  }, [isOpen, release]);

  if (!isOpen) return null;

  const rowEditBusy = editingChange !== null;
  const isDirty = isReleaseFormDirty(form, baseline);
  const canSave =
    isDirty &&
    !rowEditBusy &&
    Boolean(form.releaseDate) &&
    releaseHasRequiredVersions(form);
  const isEdit = Boolean(release?.id);

  function startCreateFeature() {
    if (rowEditBusy) return;
    const localId = nextLocalIdRef.current++;
    const row: ReleaseChangeFormRow = { localId, description: "" };
    setForm((f) => ({ ...f, changes: [...f.changes, row] }));
    setEditingChange({ localId, description: "", isNew: true });
  }

  function startEditFeature(row: ReleaseChangeFormRow) {
    if (rowEditBusy) return;
    setEditingChange({
      localId: row.localId,
      description: row.description,
      isNew: false,
    });
  }

  function cancelFeatureEdit() {
    if (!editingChange) return;
    if (editingChange.isNew) {
      setForm((f) => ({
        ...f,
        changes: f.changes.filter((c) => c.localId !== editingChange.localId),
      }));
    }
    setEditingChange(null);
  }

  function commitFeatureEdit() {
    if (!editingChange) return;
    const description = editingChange.description.trim();
    if (!description) {
      toast.error("Feature description is required.");
      return;
    }
    setForm((f) => ({
      ...f,
      changes: f.changes.map((c) =>
        c.localId === editingChange.localId ? { ...c, description } : c
      ),
    }));
    setEditingChange(null);
  }

  function removeFeature(localId: number) {
    if (rowEditBusy) return;
    setForm((f) => ({
      ...f,
      changes: f.changes.filter((c) => c.localId !== localId),
    }));
  }

  async function handleSave() {
    if (!canSave) {
      if (rowEditBusy) {
        toast.error("Finish or cancel the feature edit first.");
        return;
      }
      if (!isDirty) {
        toast.error("No changes to save.");
        return;
      }
      toast.error("Release date and at least one version are required.");
      return;
    }

    const payload = toReleaseWritePayload(form);
    updateSpinner?.(true);
    try {
      const resp = isEdit
        ? await RightTimeApi.putAsync(`/Releases/${release!.id}`, payload)
        : await RightTimeApi.postAsync(`/Releases`, payload);

      if (resp?.ok && resp.jsonData) {
        toast.success(isEdit ? "Release updated." : "Release created.");
        onSaved(resp.jsonData as ReleaseHistoryItem);
        onClose();
        return;
      }

      toast.error(resp?.jsonData?.message || "Failed to save release.");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to save release.";
      toast.error(message);
    } finally {
      updateSpinner?.(false);
    }
  }

  const footer = (
    <>
      <div className="footerLeft" />
      <div className="footerRight">
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={`btn btn-primary${!canSave ? " disabled" : ""}`}
          onClick={handleSave}
          disabled={!canSave}
        >
          Save
        </button>
      </div>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      title={isEdit ? "Edit Release" : "Add Release"}
      onClose={onClose}
      footer={footer}
      className="releaseEditModal"
      zIndex={1200}
    >
      <div className="modalBody releaseEditModalBody">
        <div className="releaseEditFields">
          <label className="releaseEditField">
            <span>Release date *</span>
            <input
              type="date"
              value={form.releaseDate}
              onChange={(e) => setForm((f) => ({ ...f, releaseDate: e.target.value }))}
            />
          </label>
          <label className="releaseEditField">
            <span>Frontend version</span>
            <input
              type="text"
              maxLength={20}
              value={form.frontendVersion}
              onChange={(e) => setForm((f) => ({ ...f, frontendVersion: e.target.value }))}
              placeholder="e.g. 6.04.131"
            />
          </label>
          <label className="releaseEditField">
            <span>Backend version</span>
            <input
              type="text"
              maxLength={20}
              value={form.backendVersion}
              onChange={(e) => setForm((f) => ({ ...f, backendVersion: e.target.value }))}
              placeholder="e.g. 6.04.155"
            />
          </label>
        </div>

        <div className="tableWrapper dimensionTable scrollable releaseChangesTable">
          <table>
            <caption>
              <span className="title">Features</span>
              <span className="captionRight">
                <FaPlusSquare
                  className={`actionIcon plusIcon${rowEditBusy ? " disabled" : ""}`}
                  title={rowEditBusy ? "Finish the current feature edit first" : "Add feature"}
                  onClick={startCreateFeature}
                />
              </span>
            </caption>
            <thead>
              <tr>
                <th>Description</th>
                <th className="actions" />
              </tr>
            </thead>
            <tbody>
              {form.changes.length === 0 ? (
                <tr>
                  <td colSpan={2} className="emptyStateCell">
                    No features yet. Use + to add one.
                  </td>
                </tr>
              ) : (
                form.changes.map((row) => {
                  const isThisRowEditing =
                    editingChange !== null && editingChange.localId === row.localId;
                  return (
                    <tr key={row.localId}>
                      <td>
                        {isThisRowEditing ? (
                          <textarea
                            className="releaseChangeDescription"
                            value={editingChange.description}
                            maxLength={4096}
                            rows={2}
                            autoFocus
                            aria-label="Feature description"
                            onChange={(e) =>
                              setEditingChange((prev) =>
                                prev ? { ...prev, description: e.target.value } : prev
                              )
                            }
                          />
                        ) : (
                          <span className="releaseChangeText">{row.description || "—"}</span>
                        )}
                      </td>
                      <td className="actions">
                        {isThisRowEditing ? (
                          <>
                            <RiSave3Fill
                              className="save actionIcon"
                              onClick={commitFeatureEdit}
                              title="Apply (save to server with Save)"
                            />
                            <ImCancelCircle
                              className="cancel actionIcon"
                              onClick={cancelFeatureEdit}
                              title="Cancel"
                            />
                          </>
                        ) : (
                          <>
                            <CiEdit
                              className={`actionIcon editIcon${rowEditBusy ? " disabled" : ""}`}
                              title={
                                rowEditBusy ? "Finish the current feature edit first" : "Edit"
                              }
                              onClick={() => {
                                if (!rowEditBusy) startEditFeature(row);
                              }}
                            />
                            <IoTrashOutline
                              className={`actionIcon deleteIcon${rowEditBusy ? " disabled" : ""}`}
                              title={
                                rowEditBusy
                                  ? "Finish the current feature edit first"
                                  : "Remove (saved when you click Save)"
                              }
                              onClick={() => removeFeature(row.localId)}
                            />
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}
